import { useBoolean } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, FilterChips, Pagination, SearchInput } from "ui";

import {
  CancelEarningsModal,
  EarningsTable,
  PayEarningsModal
} from "../../components";
import { EARNING_TYPE_LABELS, EarningChapters, EarningType } from "../../constants";
import { useEarningsList } from "../../hooks";
import { EarningsProps } from "./Earnings.types";

import styles from "./Earnings.module.scss";

const ALL_TYPES_VALUE = "All";

const Earnings = ({ setIsPayAllBtnDisabled }: EarningsProps) => {
  const { t } = useTranslation();

  const { activeTab } = useParams();

  const {
    earnings,
    totalItems,
    isLoading,
    page,
    searchValue,
    sorting,
    setSorting,
    setSearchValue,
    refetchEarnings,
    setPage,
    type,
    setType,
    resellerId,
    clearResellerFilter
  } = useEarningsList();

  const [selectedEarningsIds, setSelectedEarningsIds] = useState<string[]>([]);

  const {
    value: isCancelEarningsModalOpen,
    setTrue: openCancelEarningsModal,
    setFalse: closeCancelEarningsModal
  } = useBoolean(false);

  const {
    value: isPayEarningsModalOpen,
    setTrue: openPayEarningsModal,
    setFalse: closePayEarningsModal
  } = useBoolean(false);

  useEffect(() => {
    if (activeTab === EarningChapters.AvailableToBePaid && earnings?.length) {
      setIsPayAllBtnDisabled(false);
    } else {
      setIsPayAllBtnDisabled(true);
    }
  }, [earnings, activeTab]);

  useEffect(() => {
    setSelectedEarningsIds([]);
  }, [activeTab]);

  return (
    <div className={styles.pageContent}>
      <div className={styles.pageContent_header}>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t("Search")}
        />

        {/* Referral commissions and a reseller's own sales are two different
            programmes that used to be indistinguishable in this list. */}
        <FilterChips
          value={type ?? ALL_TYPES_VALUE}
          onChange={(nextType) =>
            setType(
              nextType === ALL_TYPES_VALUE ? null : (nextType as EarningType)
            )
          }
          options={[
            { label: "All", value: ALL_TYPES_VALUE },
            {
              label: EARNING_TYPE_LABELS[EarningType.ProductsReselling],
              value: EarningType.ProductsReselling
            },
            {
              label:
                EARNING_TYPE_LABELS[EarningType.ProductsResellingByReferral],
              value: EarningType.ProductsResellingByReferral
            }
          ]}
        />

        {selectedEarningsIds.length > 0 && (
          <div className={styles.pageContent_selectedActions}>
            {activeTab === EarningChapters.AvailableToBePaid && (
              <Button
                onClick={openPayEarningsModal}
              >{`${t("Pay")} (${selectedEarningsIds.length})`}</Button>
            )}
            {(activeTab === EarningChapters.Pending ||
              activeTab === EarningChapters.AvailableToBePaid) && (
              <Button
                onClick={openCancelEarningsModal}
                variant="default"
              >{`${t("Cancel")} (${selectedEarningsIds.length})`}</Button>
            )}
          </div>
        )}
      </div>

      {resellerId && (
        <div className={styles.pageContent_resellerFilter}>
          <span>{t("Filtered to one reseller")}</span>
          <button
            type="button"
            className={styles.pageContent_resellerFilter_clear}
            onClick={clearResellerFilter}
          >
            {t("Show all")}
          </button>
        </div>
      )}

      <div className={styles.pageContent_content}>
        <EarningsTable
          earnings={earnings ?? []}
          selectedEarningsIds={selectedEarningsIds}
          setSelectedEarningsIds={setSelectedEarningsIds}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          refetchEarnings={refetchEarnings}
        />

        <CancelEarningsModal
          earningIds={selectedEarningsIds}
          isOpen={isCancelEarningsModalOpen}
          onClose={closeCancelEarningsModal}
          onSuccess={() => {
            setSelectedEarningsIds([]);
            refetchEarnings();
          }}
        />

        <PayEarningsModal
          earningIds={selectedEarningsIds}
          isOpen={isPayEarningsModalOpen}
          onClose={closePayEarningsModal}
          onSuccess={() => {
            setSelectedEarningsIds([]);
            refetchEarnings();
          }}
        />
      </div>

      <Pagination
        totalItems={totalItems}
        currentPage={page}
        onChange={setPage}
      />
    </div>
  );
};

export default Earnings;
